import { Icon, type IconName } from '@/components';

/**
 * Auth outcome screens. The Telegram verify Edge Function can return several
 * non-success results; each gets an honest blocked/error screen instead of a
 * silent failure. Wired into the onboarding flow's decision point.
 *
 * Backend mapping (functions/api/telegram-auth):
 *   needs-invite  → 403 no_account_needs_invite
 *   suspended     → 403 account_suspended
 *   error         → telegram_bad_hash | invalid_payload | network
 */
export type AuthBlock = 'needs-invite' | 'suspended' | 'error';

const COPY: Record<AuthBlock, { icon: IconName; tone: string; title: string; th: string; body: string; cta: string }> = {
  'needs-invite': {
    icon: 'lock',
    tone: 'var(--ochre)',
    title: 'You need an invite',
    th: 'ต้องได้รับคำเชิญก่อน',
    body: 'Warm Paper ED is invite-only. Ask a department admin to send you an invite link, then sign in again.',
    cta: 'Back to sign in',
  },
  suspended: {
    icon: 'lock',
    tone: 'var(--clay)',
    title: 'Account suspended',
    th: 'บัญชีถูกระงับ',
    body: 'Your access has been suspended. Please contact an administrator to reactivate your account.',
    cta: 'Back to sign in',
  },
  error: {
    icon: 'x',
    tone: 'var(--clay)',
    title: "Couldn't sign you in",
    th: 'เข้าสู่ระบบไม่สำเร็จ',
    body: "We couldn't verify your Telegram login. Check your connection and try again.",
    cta: 'Try again',
  },
};

export function AuthBlocked({ kind, onRetry }: { kind: AuthBlock; onRetry: () => void }) {
  const c = COPY[kind];
  return (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: 84, height: 84, borderRadius: '50%', background: `color-mix(in srgb, ${c.tone} 16%, var(--card))`, color: c.tone, display: 'grid', placeItems: 'center' }}>
        <Icon name={c.icon} size={38} stroke={2.2} />
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.02em', margin: '24px 0 6px', textWrap: 'balance' }}>{c.title}</h1>
      <div className="th" style={{ fontSize: 14, color: 'var(--ink-3)' }}>{c.th}</div>
      <div style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--ink-2)', margin: '16px 0 0', maxWidth: 320 }}>
        {c.body.split(/(?<=\.)\s+/).map((line, i) => (
          <p key={i} style={{ margin: i ? '5px 0 0' : 0, textWrap: 'pretty', color: i ? 'var(--ink-3)' : undefined }}>{line}</p>
        ))}
      </div>
      <button onClick={onRetry} className="btn btn-ghost btn-lg btn-block" style={{ marginTop: 28 }}>
        {kind === 'error' && <Icon name="telegram" size={17} />} {c.cta}
      </button>
      {kind === 'needs-invite' && (
        <p style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 14 }}>
          Already have an invite link? Open it directly to join.
        </p>
      )}
    </div>
  );
}
